import { withRouter } from '@fs/zion-router'
import { ActionChip, H2, LabelTooltip, Separator } from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { v4 } from 'uuid'
import { PlaceCemetary } from '@fs/zion-icon/dist/cjs/icons'
import RestoreTombstonedEntityChip from './RestoreTombstonedEntityChip'
import MakeDeadActionChip from './MakeDeadActionChip'
import MakeLivingActionChip from './MakeLivingActionChip'
import TombstonePersonActionChip from './TombstonePersonActionChip'
import DmcCopyToClipboardTooltip from '../../util/DmcCopyToClipboardTooltip'
import { getFrontendBaseUrl } from '../../util/UrlUtil'
import { getIdElement } from '../../util/StringUtil'
import { headerCss, Subheading } from '../app/Title'
import { TreePersonLink } from '../app/Links'
import SelectiveCopy from '../../util/SelectiveCopy'

const preserveWhiteSpace = css`
  white-space: pre-wrap;
`

const LINK_TYPE_TEMPLATES = {
  OWS_SUMMARY: (id) => `/temple/ows/${id}/summary`,
  PERSON_SUMMARY: (id, forward) =>
    forward != null ? `/person/${id}/summary?forward=${forward}` : `/person/${id}/summary`,
  USER_SUMMARY: (id) => `/user/${id}/summary`,
  USER_BY_MRN: (id, forward) => `/user/${id}/summary?context=mrn&forward=${forward ?? true}`,
  USER_BY_UUID: (id, forward) => `/user/${id}/summary?context=uuid&forward=${forward}`,
  USER_BY_CMIS: (id, forward) => `/user/${id}/summary?context=cmisId&forward=${forward}`,
  USER_BY_CIS: (id) => `/user/${id}/summary?context=cisUserId`,
  TREE_SUMMARY: (id) => `/tree/${id}/summary`,
}

function buildMetadataLinkPath(linkType, id, forward) {
  const template = LINK_TYPE_TEMPLATES[linkType]
  return template ? template(id, forward) : null
}

export function DotSeparator() {
  return <span> • </span>
}

function CommaSeparator() {
  return <span>, </span>
}

/**
 * @return {numeric id}
 */
export function ConvertID(id) {
  const ALPHABET = 'M9S3Q7W4HCZ8D6XFNJVK2LGP5RTYB1'
  const NUMBER_BASE = ALPHABET.length
  const idAr = String(id).replace('-', '').toUpperCase().split('')

  // check to see if id is displayable id
  // eslint-disable-next-line react/destructuring-assignment
  if (id && id.includes('-') && (id.length === 8 || id.length === 9)) {
    let result = 0
    let isEncodingNotValid = false
    idAr.forEach((idCh) => {
      const idx = ALPHABET.indexOf(idCh)
      if (idx === -1) {
        isEncodingNotValid = true
      } else {
        result = result * NUMBER_BASE + idx
      }
    })
    return isEncodingNotValid ? null : String(result)
  }
  return null
}

function metaDataItemValueNumericValue(value, field) {
  const numericValue = field === 'owsId' ? ConvertID(value) : null
  return numericValue
}

function MetadataItemValue({ id, value, field }) {
  const values = value.value?.split('(') || []
  let alpha = value.value?.trim()
  if (values.length > 0) alpha = values[0].trim()
  const resolvedPath = value.linkType ? buildMetadataLinkPath(value.linkType, value.id, value.forward) : value.path
  const valueComponent = resolvedPath ? (
    <span id={id || `metadata_${field}Link`}>
      <DmcCopyToClipboardTooltip value={alpha} path={resolvedPath} newTab />
      {metaDataItemValueNumericValue(alpha, field) && (
        <span>
          {' ('}
          <DmcCopyToClipboardTooltip value={metaDataItemValueNumericValue(alpha, field)} />
          {') '}
        </span>
      )}
      {value.tooltipText && (
        <span>
          {' ('}
          <DmcCopyToClipboardTooltip value={value.tooltipText} />
          {') '}
        </span>
      )}
    </span>
  ) : (
    <span id={`metadata_${field}`}>
      <DmcCopyToClipboardTooltip value={alpha} />
      {ConvertID(alpha) && field !== 'lifeSpan' && (
        <>
          {' ('}
          <DmcCopyToClipboardTooltip value={ConvertID(alpha)} />
          {') '}
        </>
      )}
    </span>
  )
  return <>{valueComponent}</>
}

function openLinkInNewTab({ path }) {
  const win = window.open(`${getFrontendBaseUrl()}${path}`, '_blank', 'noopener, noreferrer')
  win.focus()
}

const redTheme = css`
  --color90: var(--red90);
  --color03a: var(--red03a);
`

function getBadgeTheme(badgeType) {
  if (badgeType === 'TREE_TYPE') return redTheme
  return undefined
}

function getBadgeLabel(badge, t) {
  if (badge.value.label) {
    return badge.value.label
  }
  if (badge.badgeType) {
    return t(badge.badgeType.toLowerCase(), badge.value.value)
  }
  return badge.value.value
}

function MetadataBadge({ badge, t }) {
  if (!badge) {
    return null
  }
  const isResearcher = badge.field === 'treeType' && badge.value.value === 'RESEARCHER'
  const badgePath = badge.value.linkType
    ? buildMetadataLinkPath(badge.value.linkType, badge.value.id, badge.value.forward)
    : badge.value.path
  const theme = getBadgeTheme(badge.badgeType)
  const actionChip = (
    <>
      &nbsp;
      <span css={theme} style={{ display: 'inline-block' }}>
        <ActionChip
          Icon={() => badge.value.icon || null}
          label={getBadgeLabel(badge, t)}
          onClick={() => openLinkInNewTab({ path: badgePath })}
          target="_blank"
        />
      </span>
    </>
  )
  if (badgePath) {
    return badge.value.tooltipText ? (
      <span aria-label={badge.value.tooltipText}>
        <LabelTooltip target={actionChip} label={badge.value.tooltipText} />
      </span>
    ) : (
      actionChip
    )
  }
  return (
    <>
      &nbsp;
      <span css={isResearcher ? redTheme : undefined} style={{ display: 'inline-block' }}>
        <ActionChip label={getBadgeLabel(badge, t)} id={isResearcher ? 'researcherChip' : 'metadataChip'} />
      </span>
    </>
  )
}

function MetaData({ context, metadata, primary }) {
  const { t } = useTranslation()
  let living = null
  if (primary?.living) living = primary.living
  if (!metadata) {
    return null
  }

  function setLivingChip() {
    const red_theme = css`
    #personLiving{
      --color90:var(--red90);
      --color03a:var(--red03a);}
    }
  `
    if (typeof primary === 'undefined' || primary?.synthetic) return null
    if (living)
      return (
        <>
          <span css={red_theme} style={{ display: 'inline-block' }}>
            <ActionChip label={t('living', 'Living')} id="personLiving" />
          </span>
          &nbsp;
          <MakeDeadActionChip />
        </>
      )
    return (
      <>
        <span style={{ display: 'inline-block' }}>
          <ActionChip label={t('deceased', 'Deceased')} id="personDeceased" />
        </span>
        &nbsp;
        <MakeLivingActionChip />
      </>
    )
  }

  const setTreeBadge = (badge) => {
    if (badge.private === true) {
      badge.value.tooltipText = t('private', 'Private')
    } else if (badge.private === false) {
      badge.value.tooltipText = t('notPrivate', 'Not Private')
    } else {
      badge.value.tooltipText = t('privacyNotSpecified', 'Privacy not specified')
    }
    if (badge.tombstoned === true) {
      badge.value.tooltipText += `, ${t('tombstoned', 'Tombstoned')}`
    } else if (badge.tombstoned === false) {
      badge.value.tooltipText += `, ${t('notTombstoned', 'Not Tombstoned')}`
    } else {
      badge.value.tooltipText += `, ${t('tombstonedNotSpecified', 'Tombstoned not specified')}`
    }
    if (badge.tombstoned) {
      badge.value.icon = <PlaceCemetary id="cemetaryIcon" />
    }
    badge.value.label = t('treeIdWithValue', { treeId: badge.value.value, defaultValue: badge.value.value })
  }

  function setMetadataBadges() {
    let isTombstoned
    if (context === 'person' && metadata.badges) {
      isTombstoned = metadata.badges.some(
        (badge) => badge.badgeType === 'TOMBSTONED' || badge.value.value === 'Tombstoned'
      )
    }
    if (primary && primary.mePersonCisId) {
      const cisId = getIdElement(primary.mePersonCisId)
      // dedup
      if (!metadata.badges.some((badge) => badge.field === 'mePersonCisId'))
        metadata.badges.push({
          field: 'mePersonCisId',
          value: {
            value: cisId,
            linkType: 'USER_BY_CIS',
            id: cisId,
          },
        })
    }

    return (
      <>
        {isTombstoned === false && <TombstonePersonActionChip />}

        {metadata.badges?.map((badge) => {
          if ((badge.badgeType === 'TOMBSTONED' || badge.value.value === 'Tombstoned') && !primary?.synthetic) {
            return (
              <React.Fragment key={v4()}>
                <MetadataBadge badge={badge} t={t} />
                &nbsp;
                <RestoreTombstonedEntityChip
                  type={metadata.items.find((item) => item.field === 'ids') ? 'personId' : 'groupId'}
                />
              </React.Fragment>
            )
          }
          if (badge.field === 'owner') {
            if (badge.value.value.toLowerCase() === 'public' || badge.value.value.toLowerCase() === 'admin') {
              return null
            }
            if (badge.value.linkType) {
              if (badge.value.linkType === 'USER_BY_CIS' || badge.value.linkType === 'USER_SUMMARY') {
                badge.value.label = t('cisIdWithValue', { cisId: badge.value.value, defaultValue: badge.value.value })
              } else if (badge.value.linkType === 'TREE_SUMMARY') {
                setTreeBadge(badge)
              }
            } else if (badge.value.path?.startsWith(`/user/`)) {
              badge.value.label = t('cisIdWithValue', { cisId: badge.value.value, defaultValue: badge.value.value })
            } else if (badge.value.path?.startsWith(`/tree/`)) {
              setTreeBadge(badge)
            }
          }
          if (badge.field === 'mePersonCisId') {
            badge.value.label = t('mePersonWithValue', {
              cisId: badge.value.value,
              defaultValue: `Me Person User: ${badge.value.value}`,
            })
          }
          if (badge.field === 'treeType') {
            badge.value.label = t(`tree.type.${badge.value.value}`, badge.value.value)
          }
          if (badge.field === 'accessControl') {
            badge.value.label = t(`accessControl.${badge.value.value}`, badge.value.value)
          }
          return (
            <React.Fragment key={v4()}>
              <MetadataBadge badge={badge} t={t} />
            </React.Fragment>
          )
        })}
      </>
    )
  }

  return (
    <SelectiveCopy>
      {metadata.header && (
        <>
          <H2 size="H3">
            <span css={[preserveWhiteSpace, headerCss]}>
              {metadata.header}
              <span data-nocopy>
                &nbsp;
                {setLivingChip()}
                &nbsp;
                {setMetadataBadges()}
              </span>
            </span>
          </H2>
          <Separator size="xxs" />
          <Subheading>
            {metadata.items?.map((item, index) => {
              return (
                <span key={v4()}>
                  {item.showLabel && <span>{item.label}: </span>}
                  {item.value && (
                    <>
                      <MetadataItemValue id={item.id || undefined} value={item.value} field={item.field} />
                      {['ids', 'pid'].includes(item.field) && (
                        <TreePersonLink data-nocopy personId={item.value.value?.split(' ')?.[0]} treeIcon newTab />
                      )}
                    </>
                  )}
                  {item.values?.map((value, itemIndex) => (
                    <span key={v4()}>
                      <MetadataItemValue value={value} field={item.field} />
                      {itemIndex !== item.values.length - 1 && <CommaSeparator />}
                    </span>
                  ))}
                  {index !== metadata.items.length - 1 && <DotSeparator />}
                </span>
              )
            })}
          </Subheading>
        </>
      )}

      <Separator size="xs" />
    </SelectiveCopy>
  )
}

export default withRouter(MetaData)
